import {
    CreateDialogFinishOptions,
    useCreateDialog,
    useCreateDialogRequest,
} from 'components/create-dialog/CreateDialogContext';
import { currentIndex, HistoryEntry, HistoryTrail, readHistoryTrail } from 'hooks/useHistoryTrail';
import merge from 'lodash/merge';
import { useCallback } from 'react';
import { createPath, NavigateOptions, To, useLocation, useNavigate } from 'react-router-dom';

/**
 * Navigates and tells the destination where it was opened from. Where the create dialog exists, a create URL
 * opens in it over the current page instead, with the same state.
 */
export const useNavigateWithReturnState = () => {
    const reactNavigate = useNavigate();
    const createDialog = useCreateDialog();

    const { pathname, search, state: locationState } = useLocation();

    return useCallback(
        (to: To, options: NavigateOptions = {}) => {
            const { state } = options;
            const adjustedState = {
                ...merge({}, locationState, options.state),
                returnTo: state?.returnTo !== undefined ? state.returnTo : `${pathname + search}`,
            };
            if (createDialog?.open(to, adjustedState)) {
                return;
            }
            reactNavigate(to, { ...options, state: adjustedState });
        },
        [reactNavigate, createDialog, locationState, pathname, search]
    );
};

interface LocationParams<Data> {
    pathname: string;
    state: Data;
    search: string;
    hash: string;
    key: string;
}

interface NavigateWithReturn {
    returnTo: To;
}

/**
 * In the create dialog the state is the one the dialog was opened with, so the return url is the page behind it.
 *
 * @param fallback May be either a URL or the pieces of a URL path.
 * @returns Return url from state or a fallback url if state is missing.
 */
export const useReturnToLocation = (fallback: To): To => {
    const routerLocation = useLocation() as LocationParams<NavigateWithReturn>;
    const dialogLocation = useCreateDialogRequest()?.location as LocationParams<NavigateWithReturn> | undefined;
    const { state } = dialogLocation ?? routerLocation;

    return state?.returnTo || fallback;
};

/**
 * True for the form routes. Every form path carries one of these segments: `new` and `edit`
 * (`/matter/new`, `/contact/:id/edit`, `/matter/:matterId/task/new`, `/task/new/:id`), `copy`
 * (`/matter/copy/:id`), `duplicate` (`/timecards/duplicate/:id`) and `finish`
 * (`/timecards/finish/:id`).
 */
export function isFormPath(url: string): boolean {
    const pathname = url.split('?')[0];
    return pathname.split('/').some((segment) => FORM_SEGMENTS.has(segment));
}

const FORM_SEGMENTS = new Set(['new', 'edit', 'copy', 'duplicate', 'finish']);

/**
 * Picks the history delta the back button should POP by, or `null` when no recorded entry is
 * a sound target and the caller must navigate to an explicit fallback instead.
 *
 * Walking back from `idx - 1`, an entry is skipped when it is a form (a form that was saved must
 * never come back under the back button) or when it shows the page the user is already on (every
 * tab of a read view is a further entry of that same page — see `pageKey` — and the forms that
 * still PUSH the read view they return to leave it twice in a row). The walk stops at the first
 * entry the trail does not know about — a blind POP could land anywhere, including on a form —
 * and gives up at once when the current page itself is unknown.
 */
export function resolveBackDelta(trail: HistoryTrail, idx: number): number | null {
    const currentPage = trail[idx]?.page;
    const step = resolveStep(
        trail,
        idx,
        (entry) => isFormPath(entry.url) || entry.page === currentPage,
        () => true
    );
    return step.replace ? null : step.delta;
}

export interface HistoryStep {
    delta: number;
    replace: boolean;
}

const REPLACE_CURRENT: HistoryStep = { delta: 0, replace: true };

/**
 * When no entry behind the skipped ones qualifies, rewinds to the earliest skipped entry so that one
 * is REPLACEd: the browser can only replace the entry it is on, and replacing just the current entry
 * would leave the skipped ones behind the destination. An unknown entry ends the walk the same way,
 * since nothing behind it can be trusted.
 */
function resolveStep(
    trail: HistoryTrail,
    idx: number,
    isSkipped: (entry: HistoryEntry) => boolean,
    isDestination: (entry: HistoryEntry) => boolean
): HistoryStep {
    if (!trail[idx]) {
        return REPLACE_CURRENT;
    }
    let candidate = idx - 1;
    while (candidate >= 0) {
        const entry = trail[candidate];
        if (!entry) {
            break;
        }
        if (!isSkipped(entry)) {
            if (isDestination(entry)) {
                return { delta: candidate - idx, replace: false };
            }
            break;
        }
        candidate -= 1;
    }
    return { delta: candidate + 1 - idx, replace: true };
}

/**
 * Only the page the form was opened from is popped back to; any other page is replaced by `target`,
 * so a create form lands on the new entity rather than on the list it was opened from.
 */
export function resolveReturnStep(trail: HistoryTrail, idx: number, target: string): HistoryStep {
    const targetPathname = target.split('?')[0];
    return resolveStep(
        trail,
        idx,
        (entry) => entry.url !== target && isFormPath(entry.url),
        (entry) => entry.url === target || entry.page === targetPathname
    );
}

export function resolveDeletedStep(trail: HistoryTrail, idx: number): HistoryStep {
    const deletedPage = deletedEntityPage(trail[idx]);
    return resolveStep(
        trail,
        idx,
        (entry) => isFormPath(entry.url) || isPageOrNestedUnder(entry.page, deletedPage),
        () => true
    );
}

// An entity is deleted from its read view or from its edit form, whose route is the read view's plus `/edit`.
function deletedEntityPage(entry: HistoryEntry | null | undefined): string | undefined {
    if (!entry) {
        return undefined;
    }
    return isFormPath(entry.url)
        ? entry.url
              .split('?')[0]
              .replace(/\/+$/, '')
              .replace(/\/edit$/, '')
        : entry.page;
}

function isPageOrNestedUnder(page: string, ancestor: string | undefined): boolean {
    return ancestor !== undefined && (page === ancestor || page.startsWith(`${ancestor}/`));
}

/**
 * Returns a handler for the back button on detail/child pages.
 *
 * Whenever the recorded history trail (see `useHistoryTrail`) holds a sound entry to return to,
 * it performs a real history POP — behaving like the browser's own back button, but skipping
 * forms and repeats of the current page. This matters because it is a POP, not a PUSH: list
 * pages restore their scroll offset on POP/REPLACE (see `useScrollRestoration`), and POP lands
 * wherever the user actually came from (e.g. a colleague page reached via a hyperlink, which
 * carries no explicit return state). A PUSH to a fixed route would instead reset the list to the
 * top and ignore the real origin.
 *
 * When nothing in the trail qualifies — a deep-link / refreshed first entry, a form reached
 * directly, or a trail that was lost — it falls back to an explicit `returnTo` in location
 * state, or finally the given parent route. A form REPLACEs itself with that fallback so it never
 * stays in history behind the page it leaves for.
 *
 * In the create dialog there is no history entry to go back from: the handler closes the dialog.
 *
 * @param fallback Parent route to use as the last resort.
 */
export function useGoBack(fallback: To): () => void {
    const navigate = useNavigate();
    const closeDialog = useCreateDialogRequest()?.close;
    const { pathname, state } = useLocation() as LocationParams<NavigateWithReturn>;

    return useCallback(() => {
        if (closeDialog) {
            closeDialog();
            return;
        }
        const delta = resolveBackDelta(readHistoryTrail(), currentIndex());
        if (delta !== null) {
            navigate(delta);
        } else {
            navigate(state?.returnTo ?? fallback, { replace: isFormPath(pathname) });
        }
    }, [navigate, closeDialog, pathname, state, fallback]);
}

function useTakeStep(): (step: HistoryStep, destination: To) => void {
    const navigate = useNavigate();

    return useCallback(
        ({ delta, replace }: HistoryStep, destination: To) => {
            if (delta === 0) {
                navigate(destination, { replace });
                return;
            }
            if (replace) {
                // A POP lands asynchronously, on `popstate`; the REPLACE has to wait for it or it
                // would overwrite the entry being left instead of the one landed on. Callers turn
                // their navigation blockers off first: the router lets a blocked POP land on
                // `landing` and only then reverts it, which this check cannot tell apart.
                const landing = currentIndex() + delta;
                const replaceLanding = () => {
                    if (currentIndex() === landing) {
                        navigate(destination, { replace: true });
                    }
                };
                window.addEventListener('popstate', replaceLanding, { once: true });
            }
            navigate(delta);
        },
        [navigate]
    );
}

/**
 * How a form leaves once it has saved: for `target`, popping back to it when it is where the form was opened
 * from. In the create dialog the form is on no history entry: it hands `target` to the dialog, which closes, or
 * starts the same creation again, and navigates nowhere: `target` is what the creation's toast offers to open.
 * `options` are for the dialog alone, and a page ignores them.
 */
export function useLeaveForm(): (target: To, options?: Pick<CreateDialogFinishOptions, 'draft'>) => void {
    const takeStep = useTakeStep();
    const finishDialog = useCreateDialogRequest()?.finish;

    return useCallback(
        (target: To, ...options: [Pick<CreateDialogFinishOptions, 'draft'>?]) => {
            if (finishDialog) {
                // Handed on as given: a form that passes no options passes the dialog none.
                finishDialog(target, ...options);
                return;
            }
            takeStep(resolveReturnStep(readHistoryTrail(), currentIndex(), toUrl(target)), target);
        },
        [takeStep, finishDialog]
    );
}

/**
 * @param fallback Replaces the deleted entity's pages when nothing precedes them in the trail.
 */
export function useLeaveDeletedEntity(fallback: To): () => void {
    const takeStep = useTakeStep();

    return useCallback(
        () => takeStep(resolveDeletedStep(readHistoryTrail(), currentIndex()), fallback),
        [takeStep, fallback]
    );
}

const toUrl = (to: To): string => (typeof to === 'string' ? to : createPath(to));
